import { Trans, t } from "@lingui/macro";
import type { Signer } from "ethers";
import { useCallback, useEffect, useRef, useState } from "react";

import Modal from "components/Modal/Modal";
import { helperToast } from "lib/helperToast";

import { updateProfile } from "domain/copyTrading/useCopyTradingData";

const VP = 260;          // crop viewport (square) in CSS px
const EXPORT = 512;      // exported avatar resolution
const MAX_NAME = 24;

type Props = {
  isVisible: boolean;
  address: string;
  signer: Signer | undefined;
  currentName?: string | null;
  currentAvatarUrl?: string | null;
  onClose: () => void;
  onSaved: (r: { name: string | null; avatarUrl?: string }) => void;
};

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

// Draw the image "cover"-fitted into a square of side S, panned by `offset`
// (expressed in viewport px) and scaled by `zoom`.
function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  S: number,
  zoom: number,
  offset: { x: number; y: number }
) {
  const base = Math.max(VP / img.naturalWidth, VP / img.naturalHeight);
  const scale = base * zoom * (S / VP);
  const drawW = img.naturalWidth * scale;
  const drawH = img.naturalHeight * scale;
  const cx = S / 2 + offset.x * (S / VP);
  const cy = S / 2 + offset.y * (S / VP);
  ctx.clearRect(0, 0, S, S);
  ctx.drawImage(img, cx - drawW / 2, cy - drawH / 2, drawW, drawH);
}

export function ProfileEditModal({
  isVisible, address, signer, currentName, currentAvatarUrl, onClose, onSaved,
}: Props) {
  const [name, setName] = useState("");
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  // Reset the form each time the modal opens.
  useEffect(() => {
    if (isVisible) {
      setName(currentName ?? "");
      setImg(null);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    }
  }, [isVisible, currentName]);

  // Keep the pan within bounds so the image always covers the viewport.
  const clampOffset = useCallback(
    (o: { x: number; y: number }, image: HTMLImageElement, z: number) => {
      const base = Math.max(VP / image.naturalWidth, VP / image.naturalHeight);
      const scale = base * z;
      const maxX = Math.max(0, (image.naturalWidth * scale - VP) / 2);
      const maxY = Math.max(0, (image.naturalHeight * scale - VP) / 2);
      return { x: clamp(o.x, -maxX, maxX), y: clamp(o.y, -maxY, maxY) };
    },
    []
  );

  // Redraw the preview whenever the crop changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    if (ctx) drawCover(ctx, img, VP, zoom, offset);
  }, [img, zoom, offset]);

  const onPickFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      helperToast.error(t`Please choose a PNG, JPEG or WebP image.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        setImg(image);
        setZoom(1);
        setOffset({ x: 0, y: 0 });
      };
      image.onerror = () => helperToast.error(t`Could not read that image.`);
      image.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!img) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current || !img) return;
    const next = { x: dragRef.current.ox + (e.clientX - dragRef.current.x), y: dragRef.current.oy + (e.clientY - dragRef.current.y) };
    setOffset(clampOffset(next, img, zoom));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const onZoom = (z: number) => {
    setZoom(z);
    if (img) setOffset((o) => clampOffset(o, img, z));
  };

  const exportDataUrl = useCallback((): string | undefined => {
    if (!img) return undefined;
    const c = document.createElement("canvas");
    c.width = EXPORT;
    c.height = EXPORT;
    const ctx = c.getContext("2d");
    if (!ctx) return undefined;
    drawCover(ctx, img, EXPORT, zoom, offset);
    let url = c.toDataURL("image/webp", 0.85);
    if (!url.startsWith("data:image/webp")) url = c.toDataURL("image/png");
    return url;
  }, [img, zoom, offset]);

  const onSave = useCallback(async () => {
    if (!signer) {
      helperToast.error(t`Connect your wallet to update your profile.`);
      return;
    }
    setSaving(true);
    try {
      const result = await updateProfile(signer, { name: name.trim(), dataUrl: exportDataUrl() });
      helperToast.success(t`Profile updated.`);
      onSaved(result);
      onClose();
    } catch (e: any) {
      helperToast.error(e?.message || t`Failed to update profile.`);
    } finally {
      setSaving(false);
    }
  }, [signer, name, exportDataUrl, onSaved, onClose]);

  return (
    <Modal isVisible={isVisible} setIsVisible={onClose} label={<Trans>Edit Profile</Trans>} className="CT-profile-modal" qa="profile-edit-modal">
      {/* Photo cropper */}
      <div className="CT-profile-field">
        <label className="CT-modal-field__label"><Trans>Profile photo</Trans></label>
        <div className="CT-cropper">
          <div
            className="CT-cropper__viewport"
            style={{ width: VP, height: VP }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          >
            {img ? (
              <canvas ref={canvasRef} width={VP} height={VP} className="CT-cropper__canvas" />
            ) : currentAvatarUrl ? (
              <img src={currentAvatarUrl} className="CT-cropper__current" alt="current avatar" />
            ) : (
              <div className="CT-cropper__empty"><Trans>No photo yet</Trans></div>
            )}
            <div className="CT-cropper__ring" />
          </div>

          <div className="CT-cropper__controls">
            <label className="CT-btn-secondary CT-cropper__pick">
              <Trans>Choose image</Trans>
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onPickFile} hidden />
            </label>
            {img && (
              <input
                className="CT-cropper__zoom"
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => onZoom(parseFloat(e.target.value))}
                aria-label={t`Zoom`}
              />
            )}
            <p className="CT-modal-field__hint"><Trans>Drag to reposition, slide to zoom. Square crop.</Trans></p>
          </div>
        </div>
      </div>

      {/* Display name */}
      <div className="CT-profile-field">
        <label className="CT-modal-field__label"><Trans>Display name</Trans></label>
        <div className="CT-modal-input-wrap">
          <input
            className="CT-modal-input"
            type="text"
            maxLength={MAX_NAME}
            placeholder={t`e.g. AlphaWhale`}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <p className="CT-modal-field__hint"><Trans>Shown on the leaderboard and your trader page. Up to {MAX_NAME} characters.</Trans></p>
      </div>

      <button className="CT-btn-primary w-full mt-16" onClick={onSave} disabled={saving}>
        {saving ? <Trans>Saving…</Trans> : <Trans>Save Profile</Trans>}
      </button>
    </Modal>
  );
}
